/**
 * API client.
 *
 * Auth is a session cookie, so every request needs credentials and nothing
 * needs a token in memory. The API is same-origin — Vite proxies its prefixes
 * in development, a reverse proxy does it in production — so there is no base
 * URL to configure.
 */

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }

  /** A signed-out session, as opposed to a request that was merely refused. */
  get isUnauthenticated(): boolean {
    return this.status === 401;
  }
}

/**
 * The operator API lives under /api so the rest of the root namespace belongs to
 * this app's own routes. Postal's webhook and inbound URLs, and unsubscribe
 * links already sitting in inboxes, stay at the root and are not served here.
 */
const BASE = "/api";

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(BASE + path, {
    ...init,
    credentials: "same-origin",
    headers: {
      ...(init.body ? { "content-type": "application/json" } : {}),
      ...init.headers,
    },
  });

  if (!res.ok) {
    // Errors are JSON from our own routes, but a proxy or a crash can return
    // HTML; fall back to the status text rather than throwing while parsing.
    const body = (await res.json().catch(() => null)) as { error?: unknown } | null;
    // Only a string is a message. Anything else rendered as "[object Object]",
    // which is how validation failures used to look.
    const message =
      typeof body?.error === "string"
        ? body.error
        : res.statusText || `request failed (${res.status})`;
    throw new ApiError(res.status, message);
  }

  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, {
      method: "POST",
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    }),
  patch: <T>(path: string, body: unknown) =>
    request<T>(path, { method: "PATCH", body: JSON.stringify(body) }),
  put: <T>(path: string, body: unknown) =>
    request<T>(path, { method: "PUT", body: JSON.stringify(body) }),
  del: <T>(path: string) => request<T>(path, { method: "DELETE" }),
};
